import { z } from 'zod';
import { SUGGESTED_STATUS, type SourceSystem, type TerraSignalRow } from '../schemas/terra-signals';
import { ME_TOKEN } from './filter';
import type { FilterCondition, FilterGroup } from './filter';
import type { FlowPlaysRow } from './plays-view';

/**
 * Plays tab "People in <Play>" contract (plan 2026-09-24-005 U12; D2, D3,
 * D5, D8; extended by plan 2026-09-25-002 U1/D1/D4). The ONE shared module
 * — the contacts route (`GET /api/flow/contacts`, all three sections) and
 * the client (three `ContactFlowTable` mounts) — imports for section
 * vocabulary, membership registry, the section predicates, the pinned-row
 * copy, the member-section Mine group, and working-state keys. The members
 * route (`GET /api/flow/plays/:playId/people`) and `PlayPeopleTable` are
 * gone (plan 2026-09-25-002 U9) — the row/response schemas, columns,
 * `PLAY_PEOPLE_STATE_KEYS` and `buildPlayPeopleMineGroup` they alone used
 * went with them.
 */

// ── Sections ─────────────────────────────────────────────────────────────────

// render order, reversed 2026-10-16 per Spencer
export const PLAY_PEOPLE_SECTIONS = ['not_in_play', 'not_responded', 'responded'] as const;
export const PlayPeopleSectionSchema = z.enum(PLAY_PEOPLE_SECTIONS);
export type PlayPeopleSection = z.infer<typeof PlayPeopleSectionSchema>;

/** Which `counts` field (and optional suffix) each section's header badge shows — one mapping for every surface. */
export const PLAY_PEOPLE_SECTION_COUNT: Record<PlayPeopleSection, { key: keyof NonNullable<FlowPlaysRow['counts']>; suffix?: string }> = {
  not_in_play: { key: 'suggested', suffix: 'suggested' },
  not_responded: { key: 'invited_sent' },
  responded: { key: 'responded' },
};

export const PLAY_PEOPLE_SECTION_LABELS: Record<PlayPeopleSection, string> = {
  responded: 'Responded',
  not_responded: 'Not Responded',
  not_in_play: 'Not in this play yet',
};

/**
 * The action_state a pending (unsent) queue item carries — tier 3 of the
 * Campaign arm. Exported (plan 2026-09-25-002 U1) — U2's `play_suggested`
 * SQL on the contacts route needs the literal.
 */
export const PENDING_SEND_ACTION_STATE = 'To do';

/**
 * The pure twin of the D5 predicates (below): which section one member row
 * belongs to. `has_responded` NULL reads as false (the predicates compile
 * through `COALESCE(col, false)`), and any status that is not `Suggested` —
 * including an unknown or NULL one — is a real member, so it lands in
 * Not Responded.
 */
export function sectionOf(
  row: Pick<TerraSignalRow, 'has_responded' | 'signal_status' | 'action_state'>,
): PlayPeopleSection {
  if (row.has_responded === true) return 'responded';
  if (row.signal_status === SUGGESTED_STATUS && row.action_state !== PENDING_SEND_ACTION_STATE) return 'not_in_play';
  return 'not_responded';
}

// ── Section predicates (D5) ──────────────────────────────────────────────────

const NOT_RESPONDED_COND = (id: string): FilterCondition => ({ id, field: 'has_responded', operator: 'equals', value: 'false' });

/**
 * The D5 section predicates as `FilterGroup`s over the `terra_signals` field
 * map (`has_responded`, `signal_status`, `action_state` — all plain `ts.*`
 * columns), compiled server-side through `filterGroupsToSql`/`conditionToSql`.
 * Server-owned constants, never rep-editable.
 *
 * **The returned groups are OR-combined** (compile with `groupLogic: 'OR'`,
 * as a fragment of their own — never spliced into the rep's working filter
 * list). A list, not the single `FilterGroup` the plan sketched as
 * `sectionFilterGroup(section)`, because `FilterGroupSchema` is flat (one
 * logic per group, no nesting) and D5's `not_responded` —
 * `has_responded = false AND (status != 'Suggested' OR (status = 'Suggested'
 * AND action_state = 'To do'))` — has an OR nested inside an AND. Its
 * disjunctive normal form is the two AND groups below; the other two
 * sections are one group each.
 *
 * `not_in_play` additionally pins `has_responded = false` (D5 lists only the
 * status/action arms): that is what makes the three sections provably
 * mutually exclusive rather than exclusive only by the data invariant that
 * a responded row is never `Suggested`. `sectionOf` applies the same
 * precedence (responded wins).
 *
 * NULL semantics are `condition-to-sql.ts`'s: boolean `equals` compiles to
 * `COALESCE(col, false) = v` (NULL `has_responded` = not responded) and
 * `not_equals` keeps NULLs (an unknown/NULL status is a real member →
 * Not Responded).
 */
export function sectionFilterGroups(section: PlayPeopleSection): FilterGroup[] {
  switch (section) {
    case 'responded':
      return [{
        id: 'play-people-responded',
        logic: 'AND',
        conditions: [{ id: 'play-people-responded-1', field: 'has_responded', operator: 'equals', value: 'true' }],
      }];
    case 'not_responded':
      return [
        {
          id: 'play-people-not-responded-member',
          logic: 'AND',
          conditions: [
            NOT_RESPONDED_COND('play-people-not-responded-member-1'),
            { id: 'play-people-not-responded-member-2', field: 'signal_status', operator: 'not_equals', value: SUGGESTED_STATUS },
          ],
        },
        {
          id: 'play-people-not-responded-pending',
          logic: 'AND',
          conditions: [
            NOT_RESPONDED_COND('play-people-not-responded-pending-1'),
            { id: 'play-people-not-responded-pending-2', field: 'signal_status', operator: 'equals', value: SUGGESTED_STATUS },
            { id: 'play-people-not-responded-pending-3', field: 'action_state', operator: 'equals', value: PENDING_SEND_ACTION_STATE },
          ],
        },
      ];
    case 'not_in_play':
      return [{
        id: 'play-people-not-in-play',
        logic: 'AND',
        conditions: [
          NOT_RESPONDED_COND('play-people-not-in-play-1'),
          { id: 'play-people-not-in-play-2', field: 'signal_status', operator: 'equals', value: SUGGESTED_STATUS },
          { id: 'play-people-not-in-play-3', field: 'action_state', operator: 'not_equals', value: PENDING_SEND_ACTION_STATE },
        ],
      }];
  }
}

/**
 * Responded OR Not Responded — OR-combined, exactly like
 * `sectionFilterGroups`. The contacts route's `not_in_play` universe wraps
 * this in `NOT EXISTS (… AND (<compiled>))` so its predicate text is
 * byte-identical to the members route's two sections (D4): pure-suggestion
 * rows stay in "Not in this play yet", pending queue sends are excluded.
 */
export function memberSectionsUnionGroups(): FilterGroup[] {
  return [...sectionFilterGroups('responded'), ...sectionFilterGroups('not_responded')];
}

// ── Membership registry (D2) ─────────────────────────────────────────────────

/**
 * Which `terra_signals` rows make up "the people in a play", per play type.
 * Both routes resolve the client-sent `play_type` here and 400 `problem()`
 * "People view is not available for <type> plays yet" when it is absent.
 *
 * ONE entry, honestly: only event invites have a membership source today
 * (the Campaign arm's SFDC members + the Terra-queue tiers, keyed by
 * `signal_instance_id` = campaign id). Gong flows etc. get an entry when a
 * real membership source exists — see the plan's Deferred list.
 *
 * Keyed by the free-text `flow_play_types` slug (there is no closed slug
 * enum), so a lookup can miss — `Partial` makes the compiler say so.
 */
export const PLAY_MEMBERSHIP_SOURCES: Partial<Record<string, {
  sourceSystems: SourceSystem[];
  supportsSuggested: boolean;
  supportsCandidates: boolean;
}>> = {
  event_invite: {
    sourceSystems: ['Salesforce Campaign Member', 'Terra queue'],
    supportsSuggested: true,
    supportsCandidates: true,
  },
};

// ── Working-state keys (D1) ──────────────────────────────────────────────────

/** `flow_view_state` key for the "Not in this play yet" `ContactFlowTable` mount (D1/D4). */
export const CONTACT_PLAY_CANDIDATES_STATE_KEY = 'contact_play_candidates';

/**
 * `flow_view_state` keys for the THREE contacts-route section mounts (plan
 * 2026-09-25-002 D4) — one row per user per SECTION, shared across plays;
 * replaces the members route's `PLAY_PEOPLE_STATE_KEYS`, retired by U9.
 * `not_in_play`'s value is the pre-existing `CONTACT_PLAY_CANDIDATES_STATE_KEY`
 * literal so existing imports of that constant keep working unchanged.
 */
export const CONTACT_PLAY_SECTION_STATE_KEYS: Record<PlayPeopleSection, string> = {
  responded: 'contact_play_responded',
  not_responded: 'contact_play_not_responded',
  not_in_play: CONTACT_PLAY_CANDIDATES_STATE_KEY,
};

// ── Pinned (locked) filter-row copy (D1) ─────────────────────────────────────

/** One locked, non-removable condition row shown in a section's filter bar (D1). */
export interface PinnedFilterConditionCopy {
  label: string;
  value: string;
  tooltip: string;
}

const RESPONDED_TOOLTIP = 'This table always shows people in the selected play who have responded.';
const NOT_RESPONDED_TOOLTIP = 'This table always shows people in the selected play who have not responded — registered, invited or sent, including queued sends that have not gone out yet.';
const NOT_IN_PLAY_TOOLTIP = 'This table always shows people who are not in the selected play yet. Suggested people are listed first.';

/**
 * The rep-visible, locked filter rows a section's `ContactFlowTable` mount
 * always shows (D1) — display-only, never rep-editable, never part of
 * working state. One row per condition; every row of one section shares the
 * same tooltip.
 */
export function playSectionPinnedConditions(section: PlayPeopleSection): PinnedFilterConditionCopy[] {
  switch (section) {
    case 'responded':
      return [
        { label: 'In this play', value: 'Yes', tooltip: RESPONDED_TOOLTIP },
        { label: 'Has responded', value: 'Yes', tooltip: RESPONDED_TOOLTIP },
      ];
    case 'not_responded':
      return [
        { label: 'In this play', value: 'Yes', tooltip: NOT_RESPONDED_TOOLTIP },
        { label: 'Has responded', value: 'No', tooltip: NOT_RESPONDED_TOOLTIP },
      ];
    case 'not_in_play':
      return [
        { label: 'In this play', value: 'No', tooltip: NOT_IN_PLAY_TOOLTIP },
      ];
  }
}

// ── "(Mine)" for the contacts-route member sections (D2) ────────────────────

export const PLAY_MEMBER_MINE_GROUP_ID = 'chip-play-member-mine';

/**
 * "(Mine)" for the `play-responded` / `play-not-responded` contacts-route
 * surfaces (D2) — an ordinary role-default seed (removable, restored by
 * Reset, persists with the section's working state), NOT the members
 * route's transient chip (`buildPlayPeopleMineGroup`, which speaks that
 * route's own field names). Speaks the CONTACT catalog's keys:
 * `account_owner_name`, `account_claydr_name` (account-level, 2026-10-16),
 * `gs_name`, `play_completed_by` (the `pm` join's
 * `action_completed_by_name`, `type: 'person'` on the contact catalog —
 * id-resolved like `owner_name` on the account catalog).
 */
export function buildPlayMemberMineGroup(): FilterGroup {
  return {
    id: PLAY_MEMBER_MINE_GROUP_ID,
    logic: 'OR',
    conditions: [
      { id: 'chip-play-member-mine-1', field: 'account_owner_name', operator: 'equals', value: ME_TOKEN },
      { id: 'chip-play-member-mine-2', field: 'account_claydr_name', operator: 'equals', value: ME_TOKEN },
      { id: 'chip-play-member-mine-3', field: 'gs_name', operator: 'equals', value: ME_TOKEN },
      { id: 'chip-play-member-mine-4', field: 'play_completed_by', operator: 'equals', value: ME_TOKEN },
    ],
  };
}
